import { useCallback, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '../constants';

export interface PaginationState {
  page: number;
  pageSize: number;
  /** Wired straight to `TablePagination`'s `onChange`. */
  onPaginationChange: (nextPage: number, nextSize: number) => void;
  /**
   * Wrap a filter setter so it also returns to page 1 — a filter changes what the
   * server returns, which invalidates the current page offset.
   */
  withPageReset: <T>(setter: (value: T) => void) => (value: T) => void;
}

/**
 * Page/pageSize state shared by every paginated table in the feature.
 *
 * Pagination is server-side everywhere here (`page`/`limit` query params), so this
 * only tracks the offset — it never slices rows.
 */
export function usePaginationState(
  initialPageSize: number = DEFAULT_PAGE_SIZE
): PaginationState {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const onPaginationChange = useCallback((nextPage: number, nextSize: number) => {
    setPage(nextPage);
    setPageSize(nextSize);
  }, []);

  const withPageReset = useCallback(
    <T,>(setter: (value: T) => void) =>
      (value: T) => {
        setter(value);
        setPage(1);
      },
    []
  );

  return { page, pageSize, onPaginationChange, withPageReset };
}
